import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectIsShadowModeEnabled } from '../config/config.selector';
import { AgentMode } from './agentMode.constants';

const selectAgentModeState = (state: RootState) => state.agentMode;

const selectAgentMode = createSelector(
  selectAgentModeState,
  (agentMode) => agentMode.mode
);

/**
 * HITL-1606 — the one condition in front of every agent action.
 *
 * True when Supervised AI mode is on AND the agent has not chosen `Intercept`:
 * they may look, but nothing they do may leave the terminal. Read in two kinds
 * of place — the choke points that refuse the action, which are the whole of
 * the safety story, and the controls, which disable themselves so the agent is
 * never invited to attempt something that would be refused.
 *
 * Keyed on the MODE and nothing else. An earlier draft of the mode slice
 * carried a second `engaged` flag, latched on the first interception, and the
 * gate deliberately did not read it: the gate would then have been opened by
 * the very first action it exists to refuse. The flag is gone with HITL-1604's
 * one-way Intercept, but the reasoning is why this predicate still asks only
 * "which mode", and never "how far in".
 *
 * ANDed with `selectIsShadowModeEnabled` — the full `hitl_3_0 && hitl_7_0 &&
 * hitl_shadow_mode` gate — so with the flag off this is always false and every
 * surface behaves exactly as it does today.
 *
 * This is the third and last selector this slice exposes. The slice
 * deliberately does not offer `isArmed` / `isEngaged` / `isSilent` variations:
 * with two modes and a one-way transition there is one question worth asking of
 * the mode from outside, and this is it.
 */
const selectIsAgentActionBlocked = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode !== AgentMode.intercept
);

export { selectAgentModeState, selectAgentMode, selectIsAgentActionBlocked };
